import React, { useContext } from 'react';
import './App.scss';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from 'react-router-dom';
import { SemanticToastContainer, toast } from 'react-semantic-toasts';
import 'react-semantic-toasts/styles/react-semantic-alert.css';
import { useTranslation } from 'react-i18next';
import { AppSettingsContext } from 'contexts/appSettings';
import { useData } from 'contexts/data';
import { getInfoTexts } from 'services/AppSettings';
import LoginPrompt from 'components/Login/LoginPrompt';
import NavBar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import ChooseProductPage from './pages/ChooseProductPage';
import ConfigurePage from './pages/ConfigurePage';
import ChooseAddonsPage from './pages/ChooseAddonsPage';
import ResultsPage from './pages/ResultsPage';

import MyPage from './pages/MyPage';
import Profile from './pages/Profile';
import RegisterPage from './pages/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerificationEmailSentPage from './pages/VerificationEmailSentPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import { useAuth } from './contexts/auth';
import SetNewPasswordPage from './pages/SetNewPasswordPage';
import ProjectPage from './pages/ProjectPage';
import NotFoundPage from './pages/NotFoundPage';
import OfferRequestsPage from './pages/Partner/OfferRequests';

// Redirect from old /project/:projectId to /projects/:projectId for backwards compatibility
const ProjectRedirect = () => {
  const { projectId } = useParams();
  return <Navigate to={`/projects/${projectId}`} replace />;
};

const App = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, getUser } = useAuth();
  const { setInfoTexts } = useContext(AppSettingsContext);
  const { onUserChange } = useData();

  const getAppSettings = async () => {
    try {
      const texts = await getInfoTexts();
      if (texts) {
        setInfoTexts([...texts]);
      }
    } catch (err) {
      toast({
        type: 'error',
        title: t('common.somethingWentWrong'),
        time: 4000,
        size: 'small',
      });
    }
  };

  React.useEffect(() => {
    getAppSettings();
  }, [i18n.language]);

  React.useEffect(() => {
    const fetchUser = async () => {
      const user = await getUser();
      onUserChange(user?.id);
    };
    if (isAuthenticated) {
      fetchUser();
    } else {
      // User logged out - clear cached user ID
      onUserChange(undefined);
    }
  }, [isAuthenticated]);

  // use this with context!

  return (
    <React.Suspense fallback={<div />}>
      <Router>
        <div className="app">
          <div className="toast">
            <SemanticToastContainer position="top-center" />
          </div>
          <LoginPrompt />
          <NavBar />
          <div className="app__content">
            <Routes>
              <Route path="/" element={<ChooseProductPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/resetpassword" element={<ResetPasswordPage />} />
              <Route
                path="/password-reset/:uid/:token"
                element={<SetNewPasswordPage />}
              />
              <Route
                path="/verification-email-sent"
                element={<VerificationEmailSentPage />}
              />
              <Route path="/verify-email/:key" element={<VerifyEmailPage />} />
              <Route path="/products" element={<ChooseProductPage />} />
              {[
                '/configure/:productId/:isExpertPage',
                '/configure/:productId',
              ].map((path) => (
                <Route key={path} path={path} element={<ConfigurePage />} />
              ))}
              <Route path="/addons" element={<ChooseAddonsPage />} />
              {[
                '/projects/:projectId/results/:resultId/:menuKey',
                '/projects/:projectId/results/:resultId/',
                '/results/:menuKey',
                'results',
              ].map((path) => (
                <Route key={path} path={path} element={<ResultsPage />} />
              ))}
              <Route path="/profile" element={<Profile />} />
              <Route path="/mypage" element={<MyPage />} />
              <Route path="/projects/:projectId" element={<ProjectPage />} />
              <Route path="/project/:projectId" element={<ProjectRedirect />} />
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="/offerrequests" element={<OfferRequestsPage />} />
            </Routes>
          </div>
        </div>
      </Router>
    </React.Suspense>
  );
};

export default App;
